import React, { useState, useEffect, useCallback } from 'react';
import {
  C, S, Badge, StatusBadge, FormField, ErrList, StatCard,
  PageHeader, FilterBar, DataTable, Modal, FormGrid,
  useToast, useConfirm, useCRUD,
} from '../../components/inventoryShared';
import {
  categoryController, subCategoryController, brandController,
  unitController, productController, warehouseController,
  locationController, supplierController, purchaseOrderController,
  purchaseReturnController, stockInController, stockOutController,
  stockTransferController, stockAdjustmentController,
  stockController, reportController,
} from '../../controllers/inventoryController';
import {
  Tag, Layers, Boxes, Settings, Package, BarChart3,
  PackageCheck, TrendingDown, ArrowLeftRight, ClipboardList,
  Users, ShoppingCart, RotateCcw, Warehouse, MapPin,
  AlertTriangle, Receipt, FileText, QrCode, Plus,
  Edit2, Trash2, Eye, Download, RefreshCw, CheckCircle,
  XCircle, Truck, ChevronDown, ChevronUp, Search,
} from 'lucide-react';

const fmt   = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
// ─────────────────────────────────────────────────────────────────────────────
// 9. PURCHASE ORDERS PAGE
// ─────────────────────────────────────────────────────────────────────────────
export const PurchaseOrdersPage = ({ companyId }) => {
  const crud = useCRUD(purchaseOrderController, companyId);
  const [showForm, setShowForm]     = useState(false);
  const [viewItem, setViewItem]     = useState(null);
  const [editItem, setEditItem]     = useState(null);
  const [saving, setSaving]         = useState(false);
  const [supplierList, setSupList]  = useState([]);
  const [warehouseList, setWareList]= useState([]);
  const [productList, setProdList]  = useState([]);
  const [errors, setErrors]         = useState([]);

  const emptyForm = { supplierId:'',supplierName:'',warehouseId:'',warehouseName:'',orderDate:new Date().toISOString().split('T')[0],expectedDate:'',items:[{productId:'',productName:'',qty:1,unitPrice:0,taxRate:0,amount:0}],subtotal:0,taxAmount:0,discount:0,shippingCharge:0,totalAmount:0,paymentMethod:'',status:'Draft',notes:'' };
  const [form, setForm] = useState(emptyForm);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  useEffect(() => {
    Promise.all([supplierController.getAll({companyId,limit:200}), warehouseController.getList(companyId), productController.getAll({companyId,limit:500})]).then(([s,w,p])=>{
      s.success && setSupList(s.data);
      w.success && setWareList(w.data);
      p.success && setProdList(p.data);
    });
  }, [companyId]);

  const addItem = () => setForm(p=>({...p, items:[...p.items,{productId:'',productName:'',qty:1,unitPrice:0,taxRate:0,amount:0}]}));
  const removeItem = (i) => setForm(p=>({...p, items:p.items.filter((_,idx)=>idx!==i)}));
  const setItem = (i,k,v) => setForm(p=>({...p, items:p.items.map((item,idx)=>{
    if (idx!==i) return item;
    const updated = {...item,[k]:v};
    if (k==='productId') { const prod=productList.find(x=>x.id===v||x._id===v); if(prod){ updated.productName=prod.name; updated.unitPrice=prod.purchasePrice; updated.taxRate=prod.taxRate||0; } }
    updated.amount = (parseFloat(updated.qty)||0) * (parseFloat(updated.unitPrice)||0);
    return updated;
  })}));

  const calcTotals = useCallback(() => {
    const sub = form.items.reduce((s,it)=>s+(parseFloat(it.amount)||0),0);
    const tax = form.items.reduce((s,it)=>s+(parseFloat(it.amount)||0)*(parseFloat(it.taxRate)||0)/100,0);
    const total = sub + tax - (parseFloat(form.discount)||0) + (parseFloat(form.shippingCharge)||0);
    setForm(p=>({...p, subtotal:sub.toFixed(2), taxAmount:tax.toFixed(2), totalAmount:Math.max(0,total).toFixed(2)}));
  }, [form.items, form.discount, form.shippingCharge]);

  useEffect(() => { calcTotals(); }, [form.items, form.discount, form.shippingCharge]);

  const openAdd  = () => { setEditItem(null); setForm(emptyForm); setErrors([]); setShowForm(true); };
  const openEdit = (r) => { setEditItem(r); setForm({...r}); setErrors([]); setShowForm(true); };
  const handleSave = async () => {
    setSaving(true); setErrors([]);
    const sup  = supplierList.find(s=>s.id===form.supplierId||s._id===form.supplierId);
    const ware = warehouseList.find(w=>w.id===form.warehouseId||w._id===form.warehouseId);
    const payload = { ...form, supplierName:sup?.name||form.supplierName, warehouseName:ware?.name||form.warehouseName, companyId };
    const res = editItem ? await purchaseOrderController.update(editItem.id,payload) : await purchaseOrderController.create(payload);
    setSaving(false);
    if (res.success) { crud.show(res.message); setShowForm(false); crud.fetchAll(); }
    else setErrors(res.errors?.length?res.errors:[res.message]);
  };

  const handleAction = async (action, id) => {
    let res;
    if (action==='approve') res = await purchaseOrderController.approve(id);
    else if (action==='cancel') res = await purchaseOrderController.cancel(id, 'Cancelled by user');
    if (res?.success) { crud.show(res.message); crud.fetchAll(); }
    else crud.show(res?.message||'Action failed','error');
  };

  const columns = [
    { label:'PO No.', key:'poNumber', render:v=><span style={{ fontFamily:'monospace', fontSize:12, color:C.primary, fontWeight:700 }}>{v||'—'}</span> },
    { label:'Supplier', key:'supplierName', render:v=><div style={{ fontWeight:700 }}>{v}</div> },
    { label:'Order Date', key:'orderDate', render:v=>fmt(v) },
    { label:'Expected', key:'expectedDate', render:v=>fmt(v) },
    { label:'Items', key:'items', render:v=><Badge color={C.primary} bg={`${C.primary}12`}>{v?.length||0} items</Badge> },
    { label:'Total', key:'totalAmount', render:v=><span style={{ fontWeight:700 }}>{money(v)}</span> },
    { label:'Payment', key:'paymentStatus', render:v=><StatusBadge status={v}/> },
    { label:'Status', key:'status', render:v=><StatusBadge status={v}/> },
    { label:'', key:'actions', render:(_, r)=>(
      <div style={{ display:'flex', gap:3 }}>
        <button style={{ ...S.btnGhost, color:C.primary }} onClick={e=>{e.stopPropagation();setViewItem(r)}}><Eye size={15}/></button>
        {r.status==='Draft' && <button style={{ ...S.btnGhost, color:C.success }} onClick={e=>{e.stopPropagation();handleAction('approve',r.id)}}><CheckCircle size={15}/></button>}
        {['Draft','Pending'].includes(r.status) && <button style={{ ...S.btnGhost, color:C.teal }} onClick={e=>{e.stopPropagation();openEdit(r)}}><Edit2 size={15}/></button>}
        {!['Received','Cancelled'].includes(r.status) && <button style={{ ...S.btnGhost, color:C.danger }} onClick={e=>{e.stopPropagation();handleAction('cancel',r.id)}}><XCircle size={15}/></button>}
      </div>
    )},
  ];

  return (
    <div style={S.page}>
      {crud.ToastEl}{crud.Dialog}
      <PageHeader title="Purchase Orders" subtitle="Manage procurement orders" Icon={ShoppingCart} color={C.primary}
        action={<button style={S.btnPri} onClick={openAdd}><Plus size={15}/> New PO</button>} />
      <div style={S.card}>
        <div style={{ padding:'14px 14px 0' }}>
          <FilterBar search={crud.search} onSearch={crud.setSearch}
            filters={[{ key:'status', value:crud.filters.status||'All', onChange:v=>crud.setFilters(p=>({...p,status:v==='All'?'':v})), options:[{value:'All',label:'All Status'},...['Draft','Approved','Ordered','Partially Received','Received','Cancelled'].map(s=>({value:s,label:s}))] }]} />
        </div>
        <DataTable columns={columns} data={crud.data} loading={crud.loading} emptyTitle="No purchase orders" onRowClick={setViewItem}
          page={crud.page} totalPages={crud.totalPages} onPageChange={crud.setPage} total={crud.total} />
      </div>

      {showForm && (
        <Modal title={editItem?'Edit Purchase Order':'New Purchase Order'} onClose={()=>setShowForm(false)} maxWidth={900}
          footer={<><button style={S.btnSec} onClick={()=>setShowForm(false)}>Cancel</button><button style={S.btnPri} onClick={handleSave} disabled={saving}>{saving?'Saving...':'Save PO'}</button></>}>
          <ErrList errors={errors} />
          <FormGrid>
            <FormField label="Supplier *">
              <select style={S.select} value={form.supplierId} onChange={e=>set('supplierId',e.target.value)}>
                <option value="">Select Supplier</option>
                {supplierList.map(s=><option key={s.id||s._id} value={s.id||s._id}>{s.name}</option>)}
              </select>
            </FormField>
            <FormField label="Warehouse">
              <select style={S.select} value={form.warehouseId} onChange={e=>set('warehouseId',e.target.value)}>
                <option value="">Select Warehouse</option>
                {warehouseList.map(w=><option key={w.id||w._id} value={w.id||w._id}>{w.name}</option>)}
              </select>
            </FormField>
            <FormField label="Order Date *"><input type="date" style={S.input} value={form.orderDate} onChange={e=>set('orderDate',e.target.value)} /></FormField>
            <FormField label="Expected Date"><input type="date" style={S.input} value={form.expectedDate} onChange={e=>set('expectedDate',e.target.value)} /></FormField>
            <FormField label="Payment Method"><select style={S.select} value={form.paymentMethod} onChange={e=>set('paymentMethod',e.target.value)}><option value="">Select</option>{['Cash','Bank Transfer','Cheque','Credit','UPI'].map(m=><option key={m}>{m}</option>)}</select></FormField>
            <FormField label="Status"><select style={S.select} value={form.status} onChange={e=>set('status',e.target.value)}>{['Draft','Approved','Ordered'].map(s=><option key={s}>{s}</option>)}</select></FormField>
          </FormGrid>

          {/* Items table */}
          <div style={{ marginBottom:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <div style={S.secTitle}>Order Items *</div>
              <button style={{ ...S.btnPri, padding:'6px 14px', fontSize:12 }} onClick={addItem}><Plus size={13}/> Add Item</button>
            </div>
            <div style={{ border:`1px solid ${C.border}`, borderRadius:8, overflow:'hidden' }}>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Product</th><th style={S.th}>Qty</th>
                  <th style={S.th}>Unit Price (₹)</th><th style={S.th}>Tax %</th>
                  <th style={S.th}>Amount (₹)</th><th style={S.th}></th>
                </tr></thead>
                <tbody>
                  {form.items.map((item,i)=>(
                    <tr key={i}>
                      <td style={S.td}>
                        <select style={{ ...S.select, fontSize:12, padding:'7px 10px' }} value={item.productId} onChange={e=>setItem(i,'productId',e.target.value)}>
                          <option value="">Select Product</option>
                          {productList.map(p=><option key={p.id||p._id} value={p.id||p._id}>{p.name}</option>)}
                        </select>
                      </td>
                      <td style={S.td}><input type="number" min="1" style={{ ...S.input, width:70, padding:'7px 8px', fontSize:12 }} value={item.qty} onChange={e=>setItem(i,'qty',e.target.value)} /></td>
                      <td style={S.td}><input type="number" style={{ ...S.input, width:100, padding:'7px 8px', fontSize:12 }} value={item.unitPrice} onChange={e=>setItem(i,'unitPrice',e.target.value)} /></td>
                      <td style={S.td}><input type="number" style={{ ...S.input, width:60, padding:'7px 8px', fontSize:12 }} value={item.taxRate} onChange={e=>setItem(i,'taxRate',e.target.value)} /></td>
                      <td style={S.td}><span style={{ fontWeight:700 }}>{money(item.amount)}</span></td>
                      <td style={S.td}><button style={{ ...S.btnGhost, color:C.danger }} onClick={()=>removeItem(i)}><Trash2 size={14}/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div style={{ display:'flex', justifyContent:'flex-end' }}>
            <div style={{ width:280, background:'#f9fafb', borderRadius:10, padding:'14px 16px' }}>
              {[['Subtotal',money(form.subtotal)],['Tax',money(form.taxAmount)],['Discount',''],['Shipping','']].map(([l,v],i)=> i>=2 ? (
                <div key={l} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, fontSize:13 }}>
                  <span style={{ color:C.textSoft }}>{l} (₹)</span>
                  <input type="number" style={{ ...S.input, width:100, padding:'5px 8px', fontSize:12 }} value={i===2?form.discount:form.shippingCharge} onChange={e=>set(i===2?'discount':'shippingCharge',e.target.value)} />
                </div>
              ) : (
                <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', fontSize:13, borderBottom:`1px solid ${C.border}`, marginBottom:8 }}>
                  <span style={{ color:C.textSoft }}>{l}</span><span style={{ fontWeight:600 }}>{v}</span>
                </div>
              ))}
              <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 0 0', fontSize:15, fontWeight:800, borderTop:`2px solid ${C.border}`, marginTop:4 }}>
                <span>Total</span><span style={{ color:C.primary }}>{money(form.totalAmount)}</span>
              </div>
            </div>
          </div>

          <FormGrid cols={1}>
            <FormField label="Notes"><textarea style={{ ...S.textarea, minHeight:60 }} value={form.notes} onChange={e=>set('notes',e.target.value)} /></FormField>
          </FormGrid>
        </Modal>
      )}

      {/* View PO */}
      {viewItem && (
        <Modal title={`PO: ${viewItem.poNumber}`} subtitle={`Supplier: ${viewItem.supplierName}`} onClose={()=>setViewItem(null)} maxWidth={720}
          footer={<><button style={S.btnSec} onClick={()=>setViewItem(null)}>Close</button>{viewItem.status==='Draft'&&<button style={S.btnPri} onClick={()=>{setViewItem(null);openEdit(viewItem);}}>Edit</button>}</>}>
          <FormGrid>
            {[['Order Date',fmt(viewItem.orderDate)],['Expected Date',fmt(viewItem.expectedDate)],['Warehouse',viewItem.warehouseName||'—'],['Status',viewItem.status],['Subtotal',money(viewItem.subtotal)],['Tax',money(viewItem.taxAmount)],['Discount',money(viewItem.discount)],['Shipping',money(viewItem.shippingCharge)],['Total',money(viewItem.totalAmount)],['Paid',money(viewItem.paidAmount)],['Balance',money((viewItem.totalAmount||0)-(viewItem.paidAmount||0))],['Payment Status',viewItem.paymentStatus||'—']].map(([l,v])=>(
              <div key={l} style={{ background:'#f9fafb', borderRadius:8, padding:'10px 13px' }}>
                <div style={{ fontSize:11, color:C.textMuted, fontWeight:700, textTransform:'uppercase', marginBottom:3 }}>{l}</div>
                <div style={{ fontSize:13, fontWeight:600 }}>{v}</div>
              </div>
            ))}
          </FormGrid>
          {viewItem.items?.length > 0 && (
            <div>
              <div style={S.secTitle}>Items</div>
              <table style={S.table}>
                <thead><tr><th style={S.th}>Product</th><th style={S.th}>Qty</th><th style={S.th}>Unit Price</th><th style={S.th}>Tax</th><th style={S.th}>Amount</th></tr></thead>
                <tbody>{viewItem.items.map((it,i)=>(
                  <tr key={i}><td style={S.td}>{it.productName||'—'}</td><td style={S.td}>{it.qty}</td><td style={S.td}>{money(it.unitPrice)}</td><td style={S.td}>{it.taxRate||0}%</td><td style={S.td}><strong>{money(it.amount)}</strong></td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default PurchaseOrdersPage;
